import { useLoad } from "@redotech/react-util/load";
import { URLVisibilityType } from "@redotech/redo-model/signup-form";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { MerchantCustomizableModal } from "../../storefront/merchant-customizable-modal";
import { BaseTeaser } from "../base-teaser";
import { SignupForm } from "../signup-form";
import { FixedContainer } from "./fixed-container";
import { SignupFormClient, SignupFormProps } from "./types";
import {
  generateAnonymousCustomerId,
  getNextStepIndex,
  isOnMobile,
  parseLocalData,
  submitStep,
} from "./utils";
const EXPIRATION_PERIOD = 24 * 60 * 60 * 1000;
const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";

export interface SignupFormLocalData {
  customerHash?: string;
  formViewedAt?: Date;
  formId?: string;
  formAutoOpenedAt?: Date;
  atLeastOneStepSubmittedAt?: Date;
}

export const SignupFormInstance = memo(function SignupFormInstance(
  props: SignupFormProps,
) {
  const [open, setOpen] = useState(false);
  const [hasEverBeenOpened, setHasEverBeenOpened] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [localData, setLocalData] = useLocalStorage<SignupFormLocalData>(
    SIGNUP_LOCAL_STORAGE_KEY,
    {},
  );

  useEffect(() => {
    if (!localData.customerHash) {
      const customerHash =
        props.shopifyCustomerId ?? generateAnonymousCustomerId();
      setLocalData((prev) => ({ ...prev, customerHash }));
    }
  }, [localData.customerHash, props.shopifyCustomerId, setLocalData]);

  const forceOpen = props.forceId || props.forceForm;
  const client = props.rpcClient;

  const formLoad = useLoad(
    async (signal) => {
      if (props.forceForm) {
        return { form: props.forceForm, formId: null, skipPhoneSteps: false };
      }
      if (!client || !localData.customerHash) return undefined;

      const result = await client.getSignupForm(
        {
          customerHash: localData.customerHash.toString(),
          shopifyCustomerId: props.shopifyCustomerId ?? null,
          formId: localData.formId?.toString() || null,
          isOnMobile: isOnMobile(),
          site: props.site,
          utmParams: props.utmParams ?? "",
          forceId: props.forceId,
        },
        { signal },
      );

      if (result.formId && !props.forceId) {
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          formId: result.formId ?? undefined,
        }));
      }

      return result;
    },
    [client, localData.customerHash, props.shopifyCustomerId, props.utmParams],
  );

  const form = formLoad.value?.form ?? null;
  const formId = formLoad.value?.formId ?? null;
  const isValidURLtoShowForm =
    form?.behavior.urlVisibility.type === URLVisibilityType.INCLUSIVE
      ? form?.behavior.urlVisibility.urls.includes(window.location.href)
      : !form?.behavior.urlVisibility.urls.includes(window.location.href);
  const isCorrectSite = props.site === form?.behavior.appearsOnSite;
  const shouldDisplay = !!(form && isCorrectSite && isValidURLtoShowForm);
  const opacityHex = form
    ? Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
        .toString(16)
        .padStart(2, "0")
    : "00";
  const { logAnalytics } = useSignupFormAnalytics(
    client,
    formId,
    localData,
    setLocalData,
  );

  const openModal = useCallback(async () => {
    if (open) return;
    setOpen(true);
    setHasEverBeenOpened(true);
    setCurrentStep(
      getNextStepIndex(form, formLoad.value?.skipPhoneSteps ?? true, 0),
    );

    if (!localData.formViewedAt && localData.customerHash && client && formId) {
      await logAnalytics("view");
    }
  }, [
    open,
    localData,
    client,
    formId,
    logAnalytics,
    setOpen,
    setHasEverBeenOpened,
    setCurrentStep,
    form,
    formLoad.value?.skipPhoneSteps,
  ]);

  // Should we auto-open the form
  useEffect(() => {
    if (
      completed ||
      !form ||
      !formId ||
      !form.behavior.display.timeOnPage.enabled ||
      !isValidURLtoShowForm
    )
      return;

    const previousAutoOpenExpired =
      localData.formAutoOpenedAt &&
      new Date().getTime() - localData.formAutoOpenedAt.getTime() >
        EXPIRATION_PERIOD;

    if (localData.formAutoOpenedAt && !previousAutoOpenExpired && !forceOpen)
      return;

    const timeout = setTimeout(() => {
      if (localData.formAutoOpenedAt && !forceOpen) return;
      setLocalData((prev: SignupFormLocalData) => ({
        ...prev,
        formAutoOpenedAt: new Date(),
      }));
      openModal().catch((e) =>
        console.error("Failed to auto-open signup form", e),
      );
    }, form.behavior.display.timeOnPage.seconds * 1000);

    return () => clearTimeout(timeout);
  }, [
    form,
    formId,
    completed,
    localData,
    setLocalData,
    openModal,
    forceOpen,
    isValidURLtoShowForm,
  ]);

  const showTeaser = useMemo(() => {
    if (forceOpen) return true;
    if (!form?.teaser?.enabled || !isCorrectSite) return false;
    if (localData.atLeastOneStepSubmittedAt) return false;
    if (form.teaser.visibility === "after-closing") {
      return hasEverBeenOpened && !completed;
    }
    if (!isValidURLtoShowForm) {
      return false;
    }

    return true;
  }, [
    forceOpen,
    form?.teaser,
    isCorrectSite,
    localData.atLeastOneStepSubmittedAt,
    hasEverBeenOpened,
    completed,
    isValidURLtoShowForm,
  ]);

  useEffect(() => {
    props.onShouldDisplayChanged?.(shouldDisplay);
  }, [shouldDisplay, props]);

  if (!form) return null;

  return (
    <>
      {form.teaser && showTeaser && (
        <FixedContainer teaser={form.teaser}>
          <BaseTeaser onClick={openModal} teaser={form.teaser} />
        </FixedContainer>
      )}
      <MerchantCustomizableModal
        backdropColor={form.theme.overlay.color + opacityHex}
        fullscreen={form.layout.type === "fullscreen"}
        onClose={() => setOpen(false)}
        open={open && !completed}
      >
        <SignupForm
          brandKit={props.brandKit}
          currentStep={currentStep}
          form={form}
          onClose={() => setOpen(false)}
          onSubmitStep={async (i: number, formData: Record<string, string>) => {
            const entryPoint = props.entryPoint;
            await submitStep({
              i,
              formData,
              form,
              formId,
              client,
              customerId,
              localData,
              setCustomerId,
              logAnalytics,
              setCurrentStep,
              setOpen,
              setCompleted,
              entryPoint,
              skipPhoneSteps: formLoad.value?.skipPhoneSteps ?? true,
            });
          }}
        />
      </MerchantCustomizableModal>
    </>
  );
});

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = localStorage.getItem(key);
    return item ? (parseLocalData(item) as T) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}

export function useSignupFormAnalytics(
  client: SignupFormClient | undefined,
  formId: string | null,
  localData: SignupFormLocalData,
  setLocalData: (
    value:
      | SignupFormLocalData
      | ((prev: SignupFormLocalData) => SignupFormLocalData),
  ) => void,
) {
  const logAnalytics = useCallback(
    async (type: "view" | "submit") => {
      if (!localData.customerHash || !client || !formId) return;

      await client.logSignupFormAnalytics({
        idempotencyKey: localData.customerHash,
        formId,
        type,
      });

      if (type === "view") {
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          formViewedAt: new Date(),
        }));
      } else {
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          atLeastOneStepSubmittedAt: new Date(),
        }));
      }
    },
    [client, formId, localData.customerHash, setLocalData],
  );

  return { logAnalytics };
}
